// src/pages/Schedule.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import WorkCalendar from "../../components/workCalendar/workCalendar";
import { PayContractorsForm, StandingForm } from "../../components/forms";
import {
  apiFetchBrandAmbassadorSchedules,
  apiFetchFieldLeaderSchedules,
} from "../../api/backend/brandAmbassador";
import {
  apiFetchAcceptedCurrentMonthOnTeamSchedule,
  apiRecordContractorsPaymentTransaction,
} from "../../api/backend/admin";
import { apiFetchCurrentUserDetails } from "../../api/backend/user";

export default function Schedule() {
  const { userId } = useParams();
  const [role, setRole] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [ambassadorRecords, setAmbassadorRecords] = useState([]);
  const [currentUserName, setCurrentUserName] = useState("");
  const [gradingEvent, setGradingEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payEvent, setPayEvent] = useState(null);
  const isActiveRef = useRef(false); // a ref to track mounted/un–mounted

  const loadSchedules = useCallback(async () => {
    if (!isActiveRef.current) return;
    setLoading(true);
    try {
      // 1) who am I?
      const userRes = await apiFetchCurrentUserDetails(userId);
      if (!isActiveRef.current) return;
      const { username, role } = userRes.data;
      setRole(role);
      setCurrentUserName(username);
      // 2) pick right endpoint
      let fetchFn =
        role === "admin"
          ? apiFetchAcceptedCurrentMonthOnTeamSchedule
          : role === "fieldLeader"
            ? () => apiFetchFieldLeaderSchedules(userId)
            : () => apiFetchBrandAmbassadorSchedules(userId);
      const { data } = await fetchFn();
      if (!isActiveRef.current) return;
      const rawSchedules = data.schedules;
      // 3) normalize into JS Dates + inject standingGraded
      const clean = rawSchedules.map((sch) => {
        const [Y, M, D] = sch.date.split("-").map(Number);
        const baseDate = new Date(Y, M - 1, D);
        const [h1, m1] = sch.startTime.split(":").map(Number);
        const [h2, m2] = sch.endTime.split(":").map(Number);
        const start = new Date(baseDate);
        start.setHours(h1, m1, 0, 0);
        const end = new Date(baseDate);
        end.setHours(h2, m2, 0, 0);
        return {
          id: sch.id,
          date: baseDate,
          startTime: start,
          endTime: end,
          instructions: sch.instructions,
          location: sch.location,
          timeZone: sch.timeZone,
          // brandAmbassadors array always carries standingGraded
          brandAmbassadors:
            role === "brandAmbassador"
              ? [
                  {
                    id: sch.eventId,
                    userName: sch.userName,
                    standingGraded: sch.standingGraded,
                  },
                ]
              : sch.brandAmbassadors || [],

          fieldLeaders: sch.fieldLeaders || [],
        };
      });
      // 4) set state
      if (!isActiveRef.current) return;
      setSchedules(clean);
      setAmbassadorRecords(
        clean.flatMap((sch) => [
          ...sch.brandAmbassadors.map((evt) => ({
            teamWorkScheduleId: sch.id,
            userName: evt.userName,
            eventId: evt.id,
            rolePerformed: "brandAmbassador",
            standingGraded: evt.standingGraded,
          })),
          ...sch.fieldLeaders.map((evt) => ({
            teamWorkScheduleId: sch.id,
            userName: evt.userName,
            eventId: evt.id,
            rolePerformed: "fieldLeader",
          })),
        ])
      );
    } catch (err) {
      if (isActiveRef.current) console.error("Error loading schedules:", err);
    } finally {
      if (isActiveRef.current) setLoading(false);
    }
  }, [userId]);

  // 2) call it on mount
  useEffect(() => {
    isActiveRef.current = true;
    loadSchedules();
    return () => {
      // cleanup: mark un‑mounted
      isActiveRef.current = false;
    };
  }, [loadSchedules]);

  // updated callback:
  const updateAmbassadorRecordState = useCallback(
    async (droppedEventId) => {
      // 1) instant local‐state prune
      setAmbassadorRecords((recs) =>
        recs.filter((r) => r.eventId !== droppedEventId)
      );
      setSchedules((all) =>
        all.map((sch) => ({
          ...sch,
          brandAmbassadors: sch.brandAmbassadors.filter(
            (ba) => ba.id !== droppedEventId
          ),
        }))
      );

      // 2) then re-fetch from the server so any BA-only days vanish
      await loadSchedules();
    },
    [loadSchedules]
  );

  if (loading) return <div>Loading schedule…</div>;
  if (!role) return <div>Couldn’t load your role.</div>;

  const recordContractorsPaymentTransaction = async (entries, status) => {
    try {
      const payload = entries.map((e) => ({
        eventId: e.id,
        userName: e.userName,
        rolePerformed: e.rolePerformed, // exactly as you store it
        wagesPayable: parseFloat(e.amount) || 0,
        paymentStatus: status,
      }));
      await apiRecordContractorsPaymentTransaction(payload);
      setPayEvent(null);
      await loadSchedules();
    } catch (err) {
      console.error("Couldn’t record payments:", err);
      alert("Payment failed—please try again.");
    }
  };

  return (
    <div id="scheduleTop" style={{ padding: 20, color: "#fff" }}>
      <h1>Schedule Page</h1>
      <WorkCalendar
        role={role}
        schedules={schedules}
        ambassadorRecords={ambassadorRecords}
        onDropShift={updateAmbassadorRecordState}
        setGradingEvent={setGradingEvent}
        setPayEvent={setPayEvent}
        currentUserName={currentUserName}
      />
      {gradingEvent && (
        <StandingForm
          pendingBAs={gradingEvent.pendingBAs}
          onClose={() => setGradingEvent(null)}
          onNext={() => {
            // drop the one we just graded
            const [, ...rest] = gradingEvent.pendingBAs;
            if (rest.length) {
              setGradingEvent({ ...gradingEvent, pendingBAs: rest });
            } else {
              setGradingEvent(null);
              loadSchedules(); // re‐fetch so the UI updates
            }
          }}
        />
      )}
      {payEvent && (
        <PayContractorsForm
          pendingPayees={payEvent.pendingPayees}
          onSubmit={(entries, status) =>
            recordContractorsPaymentTransaction(entries, status)
          } // Because I call onSubmit(entries, status) inside the child, the parent’s inline arrow (entries, status) => … will receive that status value correctly.
          onClose={() => setPayEvent(null)}
        />
      )}
    </div>
  );
}
